import Link from 'next/link';
import {isofetch} from '@helpers/common/fetch.mjs';
import {getServerSession} from 'next-auth/next';
import {authOptions} from '../api/auth/[...nextauth]';
import {Layout} from '@components/layout';
import {useForm} from 'react-hook-form';
import {useEffect, useState} from 'react';
import {useRouter} from 'next/router';
import {fixOrderNumberRemoveHash} from '@helpers/order/order-number.mjs';
import {DesktopMaxWidth, Spinner} from '@components/widgets/Common';

export async function getServerSideProps(context) {
  const req = context?.req;
  const res = context?.res;

  const session = await getServerSession(req, res, authOptions(req, res));

  if (session) {
    return {
      redirect: {
        destination: '/account/returns',
        permanent: false
      }
    };
  }

  return {
    props: {
      showCountdownTimer: false,
      showCountdownTimerMobile: false,
      showGeoTransitBanner: false,
      showFooterSignup: false,
      showFooterSignupMobile: false,
      showEmailSubscriberPopup: false,
      showPushNotificationPopup: false
    }
  };
}

export default function ReturnsCenterV2() {
  const router = useRouter();

  const {
    register,
    handleSubmit,
    formState: {errors},
    setValue,
    watch
  } = useForm({
    defaultValues: {
      orderNumber: '',
      zipCode: ''
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const zipCode = watch('zipCode');

  useEffect(() => {
    if (zipCode && zipCode.trim() !== zipCode) {
      setValue('zipCode', zipCode.trim());
    }
  }, [zipCode, setValue]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setError('');

    try {
      const response = await isofetch.post('/api/returns/validate-order', {
        orderNumber: data.orderNumber,
        zipCode: data.zipCode
      });

      await router.push('/returns/order/' + fixOrderNumberRemoveHash(response.orderNumber));
    } catch (e) {
      console.error(e);
      setError(e?.message ?? 'There was an error processing your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DesktopMaxWidth maxW="max-w-[900px]" className="mb-10">
      <div className="p-4">
        <h1 className="text-lg font-[500]">Returns and RMA Portal</h1>
        <div className="text-neutral-700 text-sm">
          Have a change of heart and want to return a recent purchase? Need to initiate a warranty?
          No problem, we've got you covered!
        </div>
        <div className="text-neutral-700 text-sm">
          Please fill out the information below to begin the return process.
        </div>

        <Link className="block my-2 text-sm underline" href="/pages/return-policy">
          View Our Full Return Policy
        </Link>
      </div>

      <div className="flex justify-center">
        <form onSubmit={handleSubmit(onSubmit)} className="w-full md:max-w-[360px] space-y-2 px-3">
          <div>
            <label className="text-sm font-bold px-1">Order Number</label>
            <div>
              <input
                className="w-full py-1 px-2 border border-neutral-300 rounded-lg bg-white text-base"
                type="text"
                placeholder="Enter your order number"
                {...register('orderNumber', {required: 'Order Number is required'})}
              />
            </div>
            {errors?.orderNumber && (
              <div className="text-sm text-skar-red-dark mb-1 px-1">
                {errors?.orderNumber?.message}
              </div>
            )}
          </div>

          <div>
            <label className="text-sm font-bold px-1">Zip Code</label>
            <div>
              <input
                className="w-full py-1 px-2 border border-neutral-300 rounded-lg bg-white text-base"
                type="text"
                placeholder="Enter your zip code"
                {...register('zipCode', {
                  required: 'Zip Code is required',
                  minLength: {
                    value: 5,
                    message: 'Zip Code must be 5 digits'
                  }
                })}
              />
            </div>
            {errors?.zipCode && (
              <div className="text-sm text-skar-red-dark mb-1 px-1">{errors?.zipCode?.message}</div>
            )}
          </div>

          <div className="flex justify-center">
            <button
              className="flex gap-2 justify-center items-center rounded px-4 py-2 text-white w-full disabled:cursor-not-allowed transition-all duration-200 bg-neutral-800"
              disabled={isSubmitting}
              type="submit">
              {isSubmitting && (
                <div className="w-4 h-4">
                  <Spinner size={16} />
                </div>
              )}
              Find Order
            </button>
          </div>

          {error && (
            <div className="text-sm text-skar-red-dark mb-1 px-2 py-2 rounded-md border border-skar-red-dark bg-skar-red-dark bg-opacity-25">
              {error}
            </div>
          )}
        </form>
      </div>
    </DesktopMaxWidth>
  );
}

ReturnsCenterV2.Layout = Layout;
